/**
 * MmsModelTree Component
 * 
 * Displays a hierarchical tree of MMS model versions with selection capability.
 * Shows badges for latest versions and allows users to select a model to visualize.
 */

import React, { useState, useEffect, useContext } from 'react';
import { RadioButton, Accordion, Icon } from '@hydrodigital/design-system';
import { SaveFilled } from '@ant-design/icons';
import { Dropdown, MenuProps } from 'antd';
import MmsService from '@/domain/Mms/MmsService';
import DashboardContext from '@/contexts/DashboardContext';

const mmsService = new MmsService();

export interface ModelVersion {
	model_id: string;
	parent_model_id: string | null;
	alias: string;
	description: string;
	date: string;
	lastest?: boolean;
	file_name?: string;
}

export interface MmsModelTreeProps {
	onModelSelect: (modelId: string, fileName: string) => void;
	selectedModelId?: string;
	searchTerm?: string;
	onSaveAsChild?: (file: ModelVersion) => Promise<void>;
	onExportToFiles?: (file: ModelVersion) => void;
	refreshKey?: number;
	expandModelId?: string;
}

export const MmsModelTree: React.FC<MmsModelTreeProps> = ({ onModelSelect, selectedModelId, searchTerm = '', onSaveAsChild, onExportToFiles, refreshKey, expandModelId }) => {
	const { value: context } = useContext(DashboardContext);
	const [files, setFiles] = useState<ModelVersion[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const [accordionValue, setAccordionValue] = useState<string>('');

	const toggleExpand = (modelId: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(modelId)) next.delete(modelId);
			else next.add(modelId);
			return next;
		});
	};

	const findRootAncestorId = (modelId: string, allFiles: ModelVersion[]): string | null => {
		let current = allFiles.find(f => f.model_id === modelId);
		while (current) {
			const parentId = !current.parent_model_id || current.parent_model_id === '' ? null : current.parent_model_id;
			if (!parentId) return current.model_id;
			current = allFiles.find(f => f.model_id === parentId);
		}
		return null;
	};

	const hasDescendantMatch = (modelId: string, lowerSearch: string): boolean => {
		const children = files.filter(f => f.parent_model_id === modelId);
		return children.some(child =>
			child.alias.toLowerCase().includes(lowerSearch) ||
			hasDescendantMatch(child.model_id, lowerSearch)
		);
	};

	useEffect(() => {
		if (context.selectedLocation) {
			loadModelVersions();
		}
	}, [context.selectedLocation]);

	useEffect(() => {
		if (refreshKey && refreshKey > 0 && context.selectedLocation) {
			loadModelVersions();
		}
	}, [refreshKey]);

	useEffect(() => {
		if (expandModelId) {
			const rootId = findRootAncestorId(expandModelId, files);
			if (rootId) setAccordionValue(rootId);
			setExpandedIds((prev) => {
				const next = new Set(prev);
				next.add(expandModelId);
				return next;
			});
		}
	}, [expandModelId]);

	useEffect(() => {
		if (selectedModelId && files.length > 0) {
			const rootId = findRootAncestorId(selectedModelId, files);
			if (rootId) setAccordionValue(rootId);
		}
	}, [selectedModelId, files]);

	const loadModelVersions = async () => {
		if (!context.selectedLocation) return;

		setIsLoading(true);
		try {
			const response = await mmsService.getModelVersions(context.selectedLocation);
			if (response?.model_version_network) {
				setFiles(response.model_version_network);
			}
		} catch (error) {
			console.error('Error loading model versions:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const getContextMenuItems = (file: ModelVersion): MenuProps['items'] => [
		{
			key: 'save-child',
			label: 'Save as child model',
			icon: <SaveFilled style={{ fontSize: 22 }} />,
			disabled: file.model_id !== selectedModelId,
			onClick: async () => {
				if (onSaveAsChild) await onSaveAsChild(file);
			},
		},
		// {
		// 	key: 'export-model',
		// 	label: 'Export to model files',
		// 	icon: <Icon name="upload" />,
		// 	onClick: () => {
		// 		if (onExportToFiles) onExportToFiles(file);
		// 	},
		// },
		// {
		// 	key: 'delete',
		// 	label: 'Delete',
		// 	icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
		// 	danger: true,
		// 	onClick: () => {
		// 		// TODO: implement delete
		// 	},
		// },
	];

	const renderRowContent = (file: ModelVersion) => {
		const isSelected = file.model_id === selectedModelId;
		const menuItems = getContextMenuItems(file);
		const shortId = file.model_id ? file.model_id.substring(0, 8) : '';

		return (
			<div className="flex items-center justify-between w-full gap-1 px-1">
				<div className="flex items-center gap-1.5 flex-1 min-w-0">
					<RadioButton
						name="mms-model-selection"
						value={file.model_id}
						label={`${file.alias} (${shortId})`}
						checked={isSelected}
						onChange={(checked) => { if (checked) onModelSelect(file.model_id, file.alias); }}
					/>
					{file.lastest && (
						<span className="text-[10px] px-1.5 py-px bg-emerald-500 text-white rounded font-bold shrink-0">
							Latest
						</span>
					)}
				</div>
				<Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
					<Icon name="ellipsis-v" className="text-base text-gray-400 cursor-pointer px-1 shrink-0" onClick={(e) => e.stopPropagation()} />
				</Dropdown>
			</div>
		);
	};

	const renderTree = (parentId: string): JSX.Element[] => {
		const lowerSearch = searchTerm.toLowerCase();
		return files
			.filter((file) => {
				const fileParent = !file.parent_model_id || file.parent_model_id === '' ? null : file.parent_model_id;
				if (fileParent !== parentId) return false;
				if (lowerSearch && !file.alias.toLowerCase().includes(lowerSearch) && !hasDescendantMatch(file.model_id, lowerSearch)) return false;
				return true;
			})
			.map((file) => {
				const isSelected = file.model_id === selectedModelId;
				const hasChildren = files.some((f) => f.parent_model_id === file.model_id);
				const isExpanded = expandedIds.has(file.model_id);

				return (
					<li key={file.model_id} className="list-none my-1">
						{hasChildren ? (
							<div>
								<div className={`flex items-center gap-1 px-1.5 py-1 rounded-md ${isSelected ? 'bg-[#e8f4f8]' : ''}`}>
									<span
										onClick={(e) => { e.stopPropagation(); toggleExpand(file.model_id); }}
										className="cursor-pointer flex items-center text-gray-400 shrink-0"
									>
										{isExpanded
											? <Icon name="angle-down" style={{ fontSize: '10px' }} />
											: <Icon name="angle-right" style={{ fontSize: '10px' }} />
										}
									</span>
									{renderRowContent(file)}
								</div>
								{isExpanded && (
									<ul className="pl-1 border-l-2 border-gray-200 mt-1 mb-1 ml-3">
										{renderTree(file.model_id)}
									</ul>
								)}
							</div>
						) : (
							<div className={`px-1.5 py-1 rounded-md ${isSelected ? 'bg-[#e8f4f8]' : ''}`}>
								<div className="flex pl-1.5 ml-1 items-center flex-1 min-w-0">
									{renderRowContent(file)}
								</div>
							</div>
						)}
					</li>
				);
			});
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center p-4">
				<span className="text-sm text-gray-500">Loading models...</span>
			</div>
		);
	}

	const rootModels = files.filter(f => !f.parent_model_id || f.parent_model_id === '');

	if (rootModels.length === 0) {
		return (
			<div className="flex items-center justify-center p-4">
				<span className="text-sm text-gray-500">No models available</span>
			</div>
		);
	}

	const lowerSearch = searchTerm.toLowerCase();
	const visibleRoots = lowerSearch
		? rootModels.filter(root =>
			root.alias.toLowerCase().includes(lowerSearch) ||
			(root.file_name?.toLowerCase().includes(lowerSearch)) ||
			hasDescendantMatch(root.model_id, lowerSearch)
		)
		: rootModels;

	return (
		<Accordion
			type="single"
			collapsible
			value={accordionValue}
			onValueChange={(v) => setAccordionValue(v as string)}
			className="mms-model-accordion"
		>
			{visibleRoots.map((root) => {
				const isRootSelected = root.model_id === selectedModelId;
				const hasChildren = files.some(f => f.parent_model_id === root.model_id);
				const isExpanded = expandedIds.has(root.model_id);

				return (
					<Accordion.Item key={root.model_id} value={root.model_id}>
						<Accordion.Trigger
							text={root.file_name || root.alias}
							iconName='edit-alt'
							className="pl-2"
						/>
						<Accordion.Content>
							{root.description && (
								<p className="px-3 pt-1 pb-2 ml-2 m-0">{root.description}</p>
							)}
							<ul className="pl-0 m-0 px-2">
								<li className="list-none my-1">
									{hasChildren ? (
										<div>
											<div className={`flex items-center gap-1 px-1.5 py-1 rounded-md ${isRootSelected ? 'bg-[#e8f4f8]' : ''}`}>
												<span
													onClick={(e) => { e.stopPropagation(); toggleExpand(root.model_id); }}
													className="cursor-pointer flex items-center text-gray-400 shrink-0"
												>
													{isExpanded
														? <Icon name="angle-down" style={{ fontSize: '10px' }} />
														: <Icon name="angle-right" style={{ fontSize: '10px' }} />
													}
												</span>
												{renderRowContent(root)}
											</div>
											{isExpanded && (
												<ul className="pl-1 border-l-2 border-gray-200 mt-1 mb-1 ml-3">
													{renderTree(root.model_id)}
												</ul>
											)}
										</div>
									) : (
										<div className={`px-1.5 py-1 rounded-md ${isRootSelected ? 'bg-[#e8f4f8]' : ''}`}>
											<div className="flex pl-1.5 ml-1 items-center flex-1 min-w-0">
												{renderRowContent(root)}
											</div>
										</div>
									)}
								</li>
							</ul>
						</Accordion.Content>
					</Accordion.Item>
				);
			})}
		</Accordion>
	);
};


export default MmsModelTree;
